import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    OnInit,
    PLATFORM_ID,
    QueryList,
    ViewChild,
    ViewChildren,
    Input,
    Output,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { makeStateKey, TransferState } from '@angular/core';

import { ApiService } from 'lib/services/api.service';
import { CategoryService } from 'lib/services/category/category.service';
import { CmsService } from 'lib/services/cms.service';
import { RouterService } from 'lib/services/router.service';
import { ExxComError } from 'lib/classes/exxcom-error.class';
import { HeaderMenuComponent as HeaderMenu } from 'lib/components/header-menu.component.class';
import { HeaderMenuNarrow } from './header-menu-narrow.class';
import isEmpty from 'lodash-es/isEmpty';
import size from 'lodash-es/size';

const scriptName = 'header-menu.component';
export interface menuObject {
    name?: string;
    href?: string;
    categories?: [{ name?: string; href?: string }] | any[];
}
const MENU_STATE_KEY = makeStateKey<Array<object>>('menu');
@Component({
    selector: 'app-header-menu',
    templateUrl: './header-menu.component.html',
    styleUrls: ['./header-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class HeaderMenuComponent extends HeaderMenu implements OnInit {
    @Input('isValidLocalSessionData') isValidLocalSessionData: boolean = false;
    @Input('isNarrow') isNarrow: boolean = false;
    @Input() isResourcePage: boolean;
    @Output() isActiveChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @ViewChild('headerMenuNarrowToggle', { static: true })
    headerMenuNarrowToggle: any;
    @ViewChild('headerMenuNarrowLevel1', { static: true })
    headerMenuNarrowLevel1: any;
    @ViewChildren('headerMenuNarrowLevel2')
    headerMenuNarrowLevel2: QueryList<ElementRef>;
    @ViewChildren('headerMenuNarrowLevel3')
    headerMenuNarrowLevel3: QueryList<ElementRef>;

    activeWideLevel1Index: number | null = null;
    currentNarrowMenuLevel: number = 0;
    isNarrowMenuOpen: boolean = false;
    level2LabelWidth: number = 0.5;
    level2MenuHeight: number | null;
    level2MenuWidth: number | null;
    menu: menuObject[];
    narrowMenu: HeaderMenuNarrow;
    wasActive: boolean = false;
    services: menuObject = {
        name: 'Services',
        href: '/Services',
        categories: [],
    };
    resources: menuObject = {
        name: 'Resources',
        href: '/Resources',
        categories: [],
    };

    isBrowser: boolean;

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object,
        private apiService: ApiService,
        categoryService: CategoryService,
        private cmsService: CmsService,
        private routerService: RouterService,
        private transferState: TransferState,
        private cdr: ChangeDetectorRef
    ) {
        super(categoryService);
        this.isBrowser = isPlatformBrowser(this.platformId);
    }

    async ngOnInit() {
        this.getMenu();
        if (!this.isBrowser) {
            return;
        }
        this.routerService.onRouteChange(scriptName, () => this.resetWideMenu());
        setTimeout(() => {
            this.narrowMenu = new HeaderMenuNarrow(
                this.isNarrow,
                {
                    menuOpen: this.isNarrowMenuOpen,
                    toggle: this.headerMenuNarrowToggle,
                    level1: this.headerMenuNarrowLevel1,
                    level2: this.headerMenuNarrowLevel2,
                    level3: this.headerMenuNarrowLevel3,
                    showChildren: this.showCategoryChildren,
                },
                this.isBrowser
            );
            if (!this.isResourcePage) {
                this.narrowMenu.init();
            }
            this.cdr.markForCheck(); // Trigger change detection with OnPush
        }, 500);
    }

    private getMenu() {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise<void>(async (resolve) => {
            try {
                this.menu = this.transferState.get(MENU_STATE_KEY, []);
                if (!isEmpty(this.menu)) {
                    this.cdr.markForCheck(); // Trigger change detection with OnPush
                    return;
                }

                const res = await this.apiService.get('menu');
                if (!res.success) {
                    throw res;
                }
                this.menu = res.data;
                const header = await this.apiService.get('headermenu');

                header.data.services.level2menus.forEach((element) => {
                    this.services.categories.push({
                        name: element.title,
                        href: element.href,
                    });
                });
                header.data.resources.level2menus.forEach((element) => {
                    this.resources.categories.push({
                        name: element.title,
                        href: element.href,
                    });
                });

                this.menu.push(this.services, this.resources);

                this.transferState.set(MENU_STATE_KEY, this.menu);
                this.cdr.markForCheck(); // Trigger change detection with OnPush
                resolve();
            } catch (err) {
                if (err.statusText === 'Unknown Error') {
                    return;
                }
                console.error(...new ExxComError(529387, scriptName, err).stamp());
            }
        });
    }

    showCategoryChildren(target: HTMLElement, categoryId: string) {
        try {
            const categoryChildren = target.parentElement.children[1];
            const categoryToggleButton = target.children[0].children[0];
            const categoryLabel = target.parentElement.children[0];
            if (categoryChildren.classList.contains('show-category-children')) {
                categoryToggleButton.classList.remove('header-menu-narrow-collapse-icon');
                categoryChildren.classList.remove('show-category-children');
                categoryLabel.classList.remove('selected-category');
            } else {
                categoryToggleButton.classList.add('header-menu-narrow-collapse-icon');
                categoryChildren.classList.add('show-category-children');
                categoryLabel.classList.add('selected-category');
            }
        } catch (err) {
            if (err.statusText === 'Unkown Error') {
                return;
            }
            console.error(...new ExxComError(167382, scriptName, err).stamp());
        }
    }

    openWideLevel1AndFocus(index: number): void {
        const isOpening = this.activeWideLevel1Index !== index;
        this.activeWideLevel1Index = isOpening ? index : null;
        this.cdr.markForCheck();
        if (isOpening) {
            setTimeout(() => {
                const level1Items = document.querySelectorAll<HTMLElement>('.header-menu-wide-level1-item');
                level1Items[index]?.querySelector<HTMLElement>('.header-menu-wide-level2-label')?.focus();
            }, 0);
        }
    }

    closeWideMenu(): void {
        this.activeWideLevel1Index = null;
        this.cdr.markForCheck();
    }

    // After navigating from the menu, close it and release focus so :focus-within styles don't keep it open
    resetWideMenu(): void {
        const wideMenu = document.getElementById('header-menu-wide');
        if (wideMenu?.contains(document.activeElement)) {
            (document.activeElement as HTMLElement).blur();
        }
        this.level2MenuHeight = null;
        this.level2MenuWidth = null;
        this.closeWideMenu();
    }

    onLevel1FocusOut(event: FocusEvent, index: number): void {
        const relatedTarget = event.relatedTarget as HTMLElement;
        const currentTarget = event.currentTarget as HTMLElement;
        if (this.activeWideLevel1Index === index && !currentTarget.contains(relatedTarget)) {
            this.activeWideLevel1Index = null;
            this.cdr.markForCheck();
        }
    }

    @HostListener('document:keydown.escape')
    onEscapeKey(): void {
        if (this.activeWideLevel1Index !== null) {
            const index = this.activeWideLevel1Index;
            this.closeWideMenu();
            setTimeout(() => {
                const labels = document.querySelectorAll<HTMLElement>('.header-menu-wide-level1-label');
                labels[index]?.focus();
            }, 0);
        }
    }

    mouseEnter(level2Category: any) {
        const dropdownWidth = 330;
        const level2Categories = size(level2Category.categories);
        if (level2Categories) {
            this.level2MenuWidth = dropdownWidth * 2;
            this.level2MenuHeight = level2Categories * 39 + 16 * 2;
        }
    }

    mouseLeave() {
        // Keep the size of a keyboard-opened menu while focus is inside it
        if (document.querySelector('.header-menu-wide-level1-item.open .header-menu-wide-level2-item:focus-within')) {
            return;
        }
        this.level2MenuHeight = null;
        this.level2MenuWidth = null;
    }

    onLevel2FocusIn(level2Category: any): void {
        const dropdownWidth = 330;
        const level2Categories = size(level2Category.categories);
        if (level2Categories) {
            this.level2MenuWidth = dropdownWidth * 2;
            this.level2MenuHeight = level2Categories * 39 + 16 * 2;
        }
        this.cdr.markForCheck();
    }

    onLevel2FocusOut(event: FocusEvent): void {
        const relatedTarget = event.relatedTarget as HTMLElement;
        const currentTarget = event.currentTarget as HTMLElement;
        if (!currentTarget.contains(relatedTarget)) {
            this.level2MenuHeight = null;
            this.level2MenuWidth = null;
            this.cdr.markForCheck();
        }
    }

    setIsActive(active: boolean) {
        if (active == this.wasActive) {
            return;
        }
        this.wasActive = active;
        this.isActiveChange.emit(active);
    }

    // collapses all open level 2 categories
    collapseCategories() {
        const openCategoryIcons = document.querySelectorAll('.header-menu-narrow-collapse-icon');
        const openCategories = document.querySelectorAll('.show-category-children');
        openCategoryIcons.forEach((icon) => {
            icon.classList.remove('header-menu-narrow-collapse-icon');
        });
        openCategories.forEach((category) => {
            category.classList.remove('show-category-children');
            category.parentElement.children[0].classList.remove('selected-category');
        });
    }

    selectCategory(target: HTMLElement) {
        this.collapseCategories();
        this.narrowMenu.close();
    }

    // returns to the first level from the second level of the narrow menu
    returnToFirstLevel() {
        if (this.narrowMenu) {
            this.collapseCategories();
            this.narrowMenu.prev();
        } else {
            return;
        }
    }

    // Keyboard focus follows the narrow menu: into it when it opens, into a category's links when
    // drilling down, and back to the toggle when it closes. Programmatic focus after a click doesn't
    // show a focus ring, so this is invisible to mouse and touch users.
    // Focus must not scroll: the panels are transformed (sliding) children of the level-1 container,
    // which is `overflow-x: hidden`. A scrolling focus() while a panel is still off-screen scrolls
    // the container sideways and leaves the user on the stack of closed, inert panels (ST-3825).
    private lastNarrowCategory: HTMLElement | null = null;

    toggleNarrowMenu() {
        this.narrowMenu?.toggle();
        if (this.narrowMenu?.menuOpen) {
            // After change detection removes `inert`
            setTimeout(() => {
                document
                    .querySelector<HTMLElement>('.header-menu-narrow-level1 [data-cy="header-content-narrow-level1-item"]')
                    ?.focus({ preventScroll: true });
            }, 0);
        }
    }

    openNarrowCategory(target: HTMLElement, category: any) {
        this.narrowMenu?.next(target, category);
        this.lastNarrowCategory = target;
        setTimeout(() => {
            const level2 = target.parentElement?.querySelector('.header-menu-narrow-level2');
            level2?.querySelector<HTMLElement>('a[href], [tabindex="0"]')?.focus({ preventScroll: true });
        }, 0);
    }

    focusLastNarrowCategory() {
        // prev() is async, so refresh the OnPush view (level 1 tab stops) once it has run
        setTimeout(() => {
            this.cdr.markForCheck();
            this.lastNarrowCategory?.focus({ preventScroll: true });
        }, 50);
    }

    closeNarrowMenuFromKeyboard() {
        if (!this.narrowMenu?.menuOpen) {
            return;
        }
        this.closeNarrowMenu();
        setTimeout(() => this.headerMenuNarrowToggle?.nativeElement?.focus({ preventScroll: true }), 0);
    }

    closeNarrowMenu() {
        if (this.narrowMenu) {
            this.collapseCategories();
            this.narrowMenu.toggle();
        } else {
            return;
        }
    }

    // trackBy functions for performance optimization
    trackByName = (index: number, item: any) => item?.name || index;
    trackByHref = (index: number, item: any) => item?.href || index;
}
